import { getApiClient } from "../http/client";
import type { DataResponse, RequestOptions } from "../http/types";
import type {
  Event,
  EventCategoryInfo,
  EventPage,
  EventStatistics,
  EventStatisticsParams,
  ListEventsParams,
} from "./types";

function normalizeEventPage(raw: unknown): EventPage {
  const data = (raw as { data?: unknown })?.data ?? raw;
  const obj = data as Partial<EventPage> & { content?: Event[] };
  return {
    content: obj?.content ?? [],
    pageNumber: obj?.pageNumber ?? 1,
    pageSize: obj?.pageSize ?? 0,
    totalElements: obj?.totalElements ?? 0,
    last: obj?.last ?? true,
    first: obj?.first ?? true,
  };
}

function unwrap<T>(res: unknown, fallback: T): T {
  const wrapped = (res as { data?: T })?.data;
  return wrapped !== undefined ? wrapped : ((res as T) ?? fallback);
}

export const eventService = {
  list: async (params?: ListEventsParams, options?: RequestOptions): Promise<EventPage> => {
    const res = await getApiClient().get<unknown>("/events", {
      ...options,
      params: params as RequestOptions["params"],
    });
    return normalizeEventPage(res);
  },

  /**
   * 차트용 집계. 목록과 **다른 엔드포인트**다.
   *
   * 예전에는 목록을 1만 건 받아 브라우저에서 집계했다. 이벤트가 그 수를 넘으면 조용히
   * 잘리고, 화면도 "최신 10000건 분석 중" 이라고 인정하고 있었다.
   *
   * 이벤트가 없는 구간도 빈 `counts` 로 돌아와 축에 구멍이 생기지 않는다.
   */
  statistics: async (params: EventStatisticsParams): Promise<EventStatistics> => {
    const res = await getApiClient().get<DataResponse<EventStatistics>>("/events/statistics", {
      params: params as unknown as Record<string, string | number | boolean | undefined>,
    });
    return unwrap<EventStatistics>(res, {
      from: params.from,
      to: params.to,
      interval: params.interval ?? "DAY",
      buckets: [],
    });
  },

  get: async (id: number): Promise<Event> => {
    const res = await getApiClient().get<DataResponse<Event>>(`/events/${id}`);
    return unwrap<Event>(res, { id } as Event);
  },

  categories: async (): Promise<EventCategoryInfo[]> => {
    const res = await getApiClient().get<DataResponse<EventCategoryInfo[]>>("/events/categories");
    return unwrap<EventCategoryInfo[]>(res, []);
  },
};
